import React, { PureComponent } from 'react';
import { withTranslation } from 'react-i18next';
import Dropzone from 'react-dropzone';
import PropTypes from 'prop-types';
import HighlightOff from '@material-ui/icons/HighlightOff';
import OpenInNew from '@material-ui/icons/OpenInNew';
import { Tooltip } from '@material-ui/core';
import { v4 as uuid } from 'uuid';
import Lottie from 'lottie-react-web';
import {
  ATTACHMENT_TYPES,
  ATTACHMENT_TYPES_MAP,
  FILE_INVALID_TYPE,
  FILE_TO_LARGE,
  MEGA_BYTES_1,
  VIMEO_VIDEO_STATUS,
  SUPPORTED_POST_FORMATS,
  VIMEO_URL,
} from '../../utils/constants';
import { showNotificationAutoDestroy } from '../ShowNotification';
import loading from '../../img/video.json';
import { isYoutubeVideo, youtubeParser } from '../../utils/smartutils';
import xlsIcon from '../../img/filetypes/xls.svg';
import xlsxIcon from '../../img/filetypes/xlsx.svg';
import docxIcon from '../../img/filetypes/docx.svg';
import docIcon from '../../img/filetypes/doc.svg';
import pptIcon from '../../img/filetypes/ppt.svg';
import pptxIcon from '../../img/filetypes/pptx.svg';
import ppsxIcon from '../../img/filetypes/ppsx.svg';
import ppsIcon from '../../img/filetypes/pps.svg';
import txtIcon from '../../img/filetypes/txt.svg';

class DropZoneMultipleField extends PureComponent {
  static propTypes = {
    onChange: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
        })
      ),
    ]).isRequired,
    fileExtensions: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.shape(PropTypes.shape)),
    ]),
    accept: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.shape(PropTypes.shape)),
    ]),
    maxSize: PropTypes.number,
    disabled: PropTypes.bool,
    showYouTubeField: PropTypes.bool,
    inputValueYT: PropTypes.string,
  };

  static defaultProps = {
    accept: '',
    fileExtensions: [],
    maxSize: 10000000,
    disabled: false,
    inputValueYT: '',
    showYouTubeField: true,
  };

  constructor() {
    super();
    this.state = {
      inputValueYT: '',
    };
    this.onDrop = this.onDrop.bind(this);
    this.onDropRejected = this.onDropRejected.bind(this);
    this.onValueYTChange = this.onValueYTChange.bind(this);
    this.addYTVideo = this.addYTVideo.bind(this);
  }

  onValueYTChange = (evt) => {
    if (evt) {
      this.setState({
        inputValueYT: evt.target.value,
      });
    }
  };

  onDrop(files) {
    const { onChange } = this.props;
    onChange(
      files.map((fl) =>
        Object.assign(fl, {
          preview:
            fl.fileId ||
            fl.type === ATTACHMENT_TYPES.VIMEO ||
            fl.type === ATTACHMENT_TYPES.YOUTUBE
              ? fl.preview
              : URL.createObjectURL(fl),
          id: fl.id ? fl.id : uuid(),
          uploadType: ATTACHMENT_TYPES_MAP.get(fl.type),
        })
      )
    );
  }

  onDropRejected(filesRejected) {
    const { t, fileExtensions, maxSize, value } = this.props;
    if (filesRejected) {
      const filesVideo = filesRejected
        .filter((item) => item.file.type.startsWith('video'))
        .map((item) => item.file);
      if (filesVideo.length) {
        this.onDrop([...value, ...filesVideo]);
      }
      const rejected = filesRejected.filter(
        (item) => !item.file.type.startsWith('video')
      );
      if (rejected.length && rejected[0].errors[0].code === FILE_INVALID_TYPE) {
        showNotificationAutoDestroy(
          'warning',
          t('notifications.attention'),
          `${t('notifications.warning-unsupported-format')} ${fileExtensions}`
        );
      } else if (
        rejected.length &&
        rejected[0].errors[0].code === FILE_TO_LARGE
      ) {
        showNotificationAutoDestroy(
          'warning',
          t('notifications.attention'),
          `${t('notifications.file-large')} ${maxSize / MEGA_BYTES_1} ${t(
            'general.mb'
          )}`
        );
      }
    }
  }

  removeFile = (index, e) => {
    const { value, onChange } = this.props;
    e.preventDefault();
    onChange(value.filter((val, i) => i !== index));
  };

  openNewView = (url) => {
    window.open(url);
  };

  checkVimeoUrl = (file) => {
    if (file.preview.startsWith(VIMEO_URL.URL_VIMEO)) {
      return `${file.preview}`;
    }
    return `${VIMEO_URL.URL_VIMEO}${file.preview}`;
  };

  getPreview = (file, i) => {
    const { t } = this.props;
    let videoVimeoStatusUploaded = false;
    let content = <img src={file.preview} alt={t('general.attachment')} />;
    if (file.status === VIMEO_VIDEO_STATUS.COMPLETE || file.lastModified) {
      videoVimeoStatusUploaded = true;
    }

    if (!file.fileId && file.type === SUPPORTED_POST_FORMATS[4].mimeType) {
      content = <img src={txtIcon} alt={file.name} title={file.name} />;
    } else if (
      !file.fileId &&
      file.type === SUPPORTED_POST_FORMATS[7].mimeType
    ) {
      content = <img src={docIcon} alt={file.name} title={file.name} />;
    } else if (
      !file.fileId &&
      file.type === SUPPORTED_POST_FORMATS[8].mimeType
    ) {
      content = <img src={docxIcon} alt={file.name} title={file.name} />;
    } else if (
      !file.fileId &&
      file.type === SUPPORTED_POST_FORMATS[9].mimeType
    ) {
      content = <img src={xlsIcon} alt={file.name} title={file.name} />;
    } else if (
      !file.fileId &&
      file.type === SUPPORTED_POST_FORMATS[10].mimeType
    ) {
      content = <img src={xlsxIcon} alt={file.name} title={file.name} />;
    } else if (
      !file.fileId &&
      file.type === SUPPORTED_POST_FORMATS[11].mimeType
    ) {
      content = <img src={pptIcon} alt={file.name} title={file.name} />;
    } else if (
      !file.fileId &&
      file.type === SUPPORTED_POST_FORMATS[12].mimeType
    ) {
      content = <img src={ppsIcon} alt={file.name} title={file.name} />;
    } else if (
      !file.fileId &&
      file.type === SUPPORTED_POST_FORMATS[13].mimeType
    ) {
      content = <img src={pptxIcon} alt={file.name} title={file.name} />;
    } else if (
      !file.fileId &&
      file.type === SUPPORTED_POST_FORMATS[14].mimeType
    ) {
      content = <img src={ppsxIcon} alt={file.name} title={file.name} />;
    }

    if (
      (file.type && file.type.startsWith('VIMEO')) ||
      (file.type && file.type.startsWith('video'))
    ) {
      content = videoVimeoStatusUploaded ? (
        <iframe
          className="doc-without-open-view"
          title={file.fileName}
          src={file.communicationId ? this.checkVimeoUrl(file) : file.preview}
        />
      ) : (
        <Lottie
          options={{
            animationData: loading,
          }}
        />
      );
    } else if (file.type && file.type.startsWith('audio')) {
      content = (
        <audio src={file.preview} controls>
          <track kind="captions" />
        </audio>
      );
    } else if (file.type === 'application/pdf' || file.type === 'PDF') {
      content = (
        <iframe
          className="doc-without-open-view"
          title={file.fileName}
          src={
            file.communicationId
              ? `https://docs.google.com/viewer?embedded=true&url=${file.preview}`
              : file.preview
          }
        />
      );
    } else if (
      (file.fileId && file.type === ATTACHMENT_TYPES.DOC) ||
      (file.fileId && file.type === ATTACHMENT_TYPES.DOCX) ||
      (file.fileId && file.type === ATTACHMENT_TYPES.XLS) ||
      (file.fileId && file.type === ATTACHMENT_TYPES.XLSX) ||
      (file.fileId && file.type === ATTACHMENT_TYPES.PPT) ||
      (file.fileId && file.type === ATTACHMENT_TYPES.PPTX) ||
      (file.fileId && file.type === ATTACHMENT_TYPES.PPS) ||
      (file.fileId && file.type === ATTACHMENT_TYPES.PPSX) ||
      (file.fileId && file.type === ATTACHMENT_TYPES.TXT)
    ) {
      content = (
        <iframe
          className="doc-without-open-view"
          title={file.fileName}
          src={
            file.communicationId
              ? `https://docs.google.com/viewer?embedded=true&url=${file.preview}`
              : file.preview
          }
        />
      );
    } else if (file.type === ATTACHMENT_TYPES.YOUTUBE) {
      content = (
        <iframe
          className="doc-without-open-view"
          title={file.fileName}
          src={`https://www.youtube.com/embed/${file.preview}`}
        />
      );
    }

    const openNewView = (url) => {
      window.open(url);
    };

    return (
      <div
        className={
          file.type !== ATTACHMENT_TYPES.VIMEO || videoVimeoStatusUploaded
            ? 'dropzone__img'
            : 'dropzone__img unloading'
        }
        key={uuid()}
      >
        {file.type !== ATTACHMENT_TYPES.VIMEO || videoVimeoStatusUploaded ? (
          <Tooltip title={t('general.view-file')}>
            <OpenInNew
              className="dropzone__ico-open-view"
              onClick={() => openNewView(content.props.src)}
            />
          </Tooltip>
        ) : null}
        <Tooltip title={t('general.remove-file')}>
          <HighlightOff
            className="dropzone__ico-delete"
            onClick={(e) => this.removeFile(i, e)}
          />
        </Tooltip>
        {content}
      </div>
    );
  };

  addYTVideo = () => {
    const { inputValueYT } = this.state;
    const { onChange, value } = this.props;
    if (isYoutubeVideo(inputValueYT)) {
      const videoYTId = youtubeParser(inputValueYT);
      const objYt = {
        fileId: uuid(),
        type: ATTACHMENT_TYPES.YOUTUBE,
        fileType: ATTACHMENT_TYPES.YOUTUBE,
        preview: videoYTId,
        fileUrl: videoYTId,
      };
      onChange([...value, objYt]);
      this.setState({
        inputValueYT: '',
      });
    }
  };

  render() {
    const { name, value, accept, t, maxSize, disabled, showYouTubeField } =
      this.props;
    const files = value;

    const { inputValueYT } = this.state;

    return (
      <div className="dropzone dropzone--multiple">
        <div className="width100">
          {showYouTubeField && (
            <div className="d-flex position-absolute width100">
              <input
                placeholder={t('general.insert-video-link-youtube')}
                value={inputValueYT}
                onChange={this.onValueYTChange}
              />
              <button
                type="button"
                color="primary"
                className="btn btn-primary rounded-0"
                onClick={() => this.addYTVideo()}
              >
                {t('general.add')}
              </button>
            </div>
          )}
          <Dropzone
            className="dropzone__input"
            accept={accept}
            name={name}
            maxSize={maxSize}
            disabled={disabled}
            onDropRejected={this.onDropRejected}
            onDrop={(filesToUpload) => {
              this.onDrop(value ? value.concat(filesToUpload) : filesToUpload);
            }}
          >
            {({ getRootProps, getInputProps }) => (
              <div {...getRootProps()} className="dropzone__input">
                {(!files || files.length === 0) && (
                  <div className="dropzone__drop-here">
                    <span className="lnr lnr-upload" />
                    {t('general.load-files')}
                  </div>
                )}
                <input {...getInputProps()} />
              </div>
            )}
          </Dropzone>
          {files && Array.isArray(files) && (
            <div className="dropzone__imgs-wrapper">
              {files.map((file, i) => this.getPreview(file, i))}
            </div>
          )}
        </div>
      </div>
    );
  }
}

const renderDropZoneMultipleField = (props) => {
  const {
    input,
    t,
    accept,
    fileExtensions,
    maxSize,
    disabled,
    showYouTubeField,
  } = props;

  return (
    <DropZoneMultipleField
      {...input}
      t={t}
      accept={accept}
      fileExtensions={fileExtensions}
      maxSize={maxSize}
      disabled={disabled}
      showYouTubeField={showYouTubeField}
    />
  );
};

renderDropZoneMultipleField.propTypes = {
  input: PropTypes.shape({
    onChange: PropTypes.func,
    name: PropTypes.string,
  }).isRequired,
  t: PropTypes.func.isRequired,
  fileExtensions: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.shape(PropTypes.shape)),
  ]),
  accept: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.shape(PropTypes.shape)),
  ]),
  maxSize: PropTypes.number,
  disabled: PropTypes.bool,
  showYouTubeField: PropTypes.bool,
};

renderDropZoneMultipleField.defaultProps = {
  accept: '',
  fileExtensions: [],
  maxSize: 10000000,
  disabled: false,
  showYouTubeField: true,
};

export default withTranslation('common')(renderDropZoneMultipleField);
